/**
 * Idle-time prefetch of every main-nav page chunk + first-visit data
 * warmup (iter 385/385c).
 *
 * All routes are React.lazy code-split — without this, the FIRST click
 * on each nav item waits for its JS chunk to download. Webpack dedupes
 * by module, so these import() calls warm the exact chunks the lazy()
 * routes use. After the chunks, the main pages' data is fetched once
 * and written into the pc1 snapshots so even a first-ever visit paints
 * instantly. Everything runs sequentially so we never trip the ingress
 * rate limit.
 */
import axios from "axios";
import BACKEND_URL from "./apiConfig";
import { readPageCache, writePageCache } from "./pageCache";
import { queryClient } from "./queryClient";
import { canAccessTeamReviews } from "./teamReviewsAccess";

const API = `${BACKEND_URL}/api`;
let started = false;

export function prefetchMainRoutes() {
  if (started) return;
  started = true;
  const thunks = [
    () => import("../pages/ServiceCalls"),
    () => import("../pages/StreetLegalKanban"),
    () => import("../pages/DailyDashboard"),
    () => import("../pages/Warranty"),
    () => import("../pages/Rebuilds"),
    () => import("../components/ResponsiveInventory"),
    () => import("../pages/StockOverview"),
    () => import("../pages/Bulletins"),
    () => import("../pages/Diagnostics"),
    () => import("../pages/DeliveryPhotosPage"),
    () => import("../pages/KnowledgeBase"),
    () => import("../pages/BuildSheetLibrary"),
    () => import("../pages/CartAudit"),
    () => import("../pages/CartAuditLog"),
    () => import("../pages/ServiceCallTagging"),
    () => import("../pages/Settings"),
    () => import("../pages/TeamReviews"),
    () => import("../pages/RecallChecklistAdmin"),
    () => import("../pages/Deliveries"),
    () => import("../pages/FleetMaintenance"),
    () => import("../pages/LocationPlanner"),
    () => import("../pages/CartTagGenerator"),
  ];
  const load = async () => {
    // Sequential, one chunk at a time — parallel bursts trip the
    // ingress rate limit (429) and flood the console (iter 385 test).
    for (const t of thunks) {
      try { await t(); } catch { /* on-demand load will retry */ }
      await new Promise((r) => setTimeout(r, 250));
    }
    await warmData();
  };
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(load, { timeout: 6000 });
  } else {
    setTimeout(load, 2500);
  }
}

/** Fetch each main page's primary data once so first-ever visits paint
 *  instantly. Keys + shapes MUST match what the pages read. Skips any
 *  key that already has a snapshot (the page keeps it fresher). */
async function warmData() {
  // iter 387 — warm at most once per 30 min per browser. Every full page
  // load re-ran this module (module state resets), so a reload storm
  // (e.g. during a backend slowdown) re-fired a dozen heavy endpoints
  // per user and made the slowdown worse. The stamp persists across
  // reloads and kills that feedback loop.
  const WARM_STAMP_KEY = "warm_data_last";
  try {
    const last = parseInt(localStorage.getItem(WARM_STAMP_KEY) || "0", 10);
    if (Date.now() - last < 30 * 60 * 1000) return;
    localStorage.setItem(WARM_STAMP_KEY, Date.now().toString());
  } catch { /* storage unavailable — warm anyway */ }
  // iter 400 — Team Reviews is allow-listed server-side; warming it for
  // everyone else produced a 403 on every login (132× in prod telemetry).
  const teamReviewsOk = canAccessTeamReviews({ name: localStorage.getItem("user_name") });
  const jobs = [
    ["bulletins", () => axios.get(`${API}/bulletins?include_hidden=true`).then((r) => r.data)],
    ["kb_articles", () => axios.get(`${API}/knowledge`).then((r) => r.data)],
    ["rebuilds:active", () => axios.get(`${API}/rebuilds`, { params: { status: "active" } }).then((r) => r.data || [])],
    ["diag_models", () => axios.get(`${API}/diagnostics/models`).then((r) => r.data)],
    ...(teamReviewsOk ? [["team_employees", () => axios.get(`${API}/team-reviews/employees`).then((r) => r.data?.employees || [])]] : []),
    ["tag_stats", () => axios.get(`${API}/service-calls/outcome-tags/stats?days=90`).then((r) => r.data)],
    ["tag_queue:untagged", () => axios.get(`${API}/service-calls/outcome-tags/queue?limit=200&filter=untagged`).then((r) => (Array.isArray(r.data) ? r.data : []))],
    ["audit_log", () => axios.get(`${API}/cart-audit/log`).then((r) => r.data)],
    ["delivery_photos", () => axios.get(`${API}/delivery-photos`, { params: { limit: 200, skip: 0 } }).then((r) => ({ photos: r.data.photos || [], total: r.data.total || 0 }))],
    ["dash_queue", async () => {
      const [q, aw] = await Promise.all([
        axios.get(`${API}/shop/queue/by-tech`),
        axios.get(`${API}/checklists/active-work`).catch(() => ({ data: [] })),
      ]);
      return {
        q: (q.data || []).filter((t) => !(t.tech_name || "").toLowerCase().includes("james green")),
        aw: aw.data || [],
      };
    }],
    ["planner_deliveries", () => axios.get(`${API}/map/locations`, { params: { include_delivery: true, include_service: false, limit: 2000, fields: "light" } }).then((r) => (r.data.locations || []).filter((l) => l.type === "delivery" && l.lat && l.lng))],
  ];
  for (const [key, fn] of jobs) {
    if (readPageCache(key)) continue;
    try { writePageCache(key, await fn()); } catch { /* page will fetch on visit */ }
    await new Promise((r) => setTimeout(r, 1500));
  }

  // iter 386e — Stock Overview / Forecasting / Inventory read through
  // TanStack Query (persisted), not pc1 snapshots. Warm those keys too
  // so the day's first visit paints with data.
  // iter 387 — only the on-order variant (the page's default view); the
  // second variant was doubling the heaviest compute on the backend for
  // a view most users never toggle to. It computes on demand instead.
  const tqJobs = [
    [["stock", "overview", "with-on-order"], () => axios.get(`${API}/stock/overview?include_on_order=true`).then((r) => r.data)],
    [["inventory", "enriched-parts"], () => axios.get(`${API}/inventory-parts/parts/enriched`).then((r) => r.data)],
  ];
  for (const [queryKey, queryFn] of tqJobs) {
    try {
      if (!queryClient.getQueryData(queryKey)) {
        await queryClient.prefetchQuery({ queryKey, queryFn, staleTime: 30 * 1000 });
      }
    } catch { /* page will fetch on visit */ }
    await new Promise((r) => setTimeout(r, 1500));
  }
}
